import React, { useCallback, useEffect, useState } from 'react';
import { UserButton } from '@clerk/clerk-react';
import { apiFetch } from '../api';


function formatDate(value) {
  if (!value) return 'Not started';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(new Date(value));
}


function ProjectStatus({ status }) {
  const ready = status === 'Package ready' || status === 'Validated';
  const waiting = status === 'Awaiting source';
  const classes = ready
    ? 'bg-emerald-100 text-emerald-800'
    : waiting
    ? 'bg-slate-100 text-slate-700'
    : 'bg-cyan-100 text-cyan-900';
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${classes}`}>{status}</span>;
}


function LoadingRows() {
  return (
    <div className="divide-y divide-slate-200" aria-label="Loading projects">
      {[0, 1, 2].map(index => (
        <div key={index} className="grid animate-pulse grid-cols-[1fr_8rem] gap-6 px-5 py-6">
          <div><div className="h-4 w-48 rounded bg-slate-200" /><div className="mt-3 h-3 w-72 max-w-full rounded bg-slate-100" /></div>
          <div className="h-9 rounded-lg bg-slate-100" />
        </div>
      ))}
    </div>
  );
}


export default function ProjectWorkspace({ onOpen }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [creating, setCreating] = useState(false);

  const loadProjects = useCallback(async () => {
    try {
      setError(null);
      const response = await apiFetch('/projects');
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Projects could not be loaded.');
      setProjects(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const createProject = async event => {
    event.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      const response = await apiFetch('/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), client_name: clientName.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'The project could not be created.');
      setName('');
      setClientName('');
      onOpen(data.id);
    } catch (err) {
      setError(err.message);
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-[#071a26] px-6 py-5 text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-[#74d6d1]">IMP</div>
            <div className="mt-0.5 text-lg font-semibold">Migration workspace</div>
          </div>
          <UserButton afterSignOutUrl={window.location.href} />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-col justify-between gap-5 border-b border-slate-200 pb-8 md:flex-row md:items-end">
          <div>
            <h1 className="text-4xl font-semibold tracking-[-0.03em] text-[#071a26]">Your migration projects</h1>
            <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
              Reopen an engagement, review its source export, or start a new migration without clearing prior work.
            </p>
          </div>
          <div className="text-sm text-slate-500">{projects.length} project{projects.length === 1 ? '' : 's'}</div>
        </div>

        <form onSubmit={createProject} className="mt-8 rounded-xl bg-[#071a26] p-5 text-white">
          <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
            <label className="text-sm font-medium">
              <span className="mb-2 block text-white/80">Project name</span>
              <input
                className="w-full rounded-lg border border-white/25 bg-white/10 px-3.5 py-3 text-white outline-none placeholder:text-white/60 focus:border-[#74d6d1] focus:ring-2 focus:ring-[#74d6d1]/30"
                value={name}
                onChange={event => setName(event.target.value)}
                placeholder="Mastercard DAM migration"
              />
            </label>
            <label className="text-sm font-medium">
              <span className="mb-2 block text-white/80">Client</span>
              <input
                className="w-full rounded-lg border border-white/25 bg-white/10 px-3.5 py-3 text-white outline-none placeholder:text-white/60 focus:border-[#74d6d1] focus:ring-2 focus:ring-[#74d6d1]/30"
                value={clientName}
                onChange={event => setClientName(event.target.value)}
                placeholder="Mastercard"
              />
            </label>
            <button
              type="submit"
              disabled={!name.trim() || creating}
              className="self-end rounded-lg bg-[#ff6b4a] px-5 py-3 font-semibold text-white transition hover:bg-[#f45c3b] focus:outline-none focus:ring-2 focus:ring-white disabled:cursor-not-allowed disabled:opacity-45"
            >
              {creating ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
            {error} <button className="ml-2 font-semibold underline" onClick={loadProjects}>Try again</button>
          </div>
        )}

        <section className="mt-8 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h2 className="font-semibold text-[#071a26]">Projects</h2>
            <span className="text-xs text-slate-500">Most recently updated first</span>
          </div>
          {loading ? <LoadingRows /> : projects.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <h3 className="font-semibold text-slate-800">No migration projects yet</h3>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">Name the first project above. You will add its source export on the next screen.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {projects.map(project => (
                <button
                  key={project.id}
                  onClick={() => onOpen(project.id)}
                  className="grid w-full gap-4 px-5 py-5 text-left transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:grid-cols-[minmax(0,1.4fr)_minmax(9rem,.7fr)_8rem_2rem] md:items-center"
                >
                  <div className="min-w-0">
                    <div className="truncate text-lg font-semibold text-[#071a26]">{project.name}</div>
                    <div className="mt-1 truncate text-sm text-slate-600">
                      {project.client_name || 'Internal project'} · {project.source?.original_filename || 'No source export'}
                    </div>
                  </div>
                  <div><ProjectStatus status={project.status} />{['starting', 'running'].includes(project.activity?.status) && <div className="mt-1 text-xs text-cyan-800">Work in progress</div>}</div>
                  <div className="text-sm"><strong className="block text-slate-800">{project.asset_count.toLocaleString()}</strong><span className="text-slate-500">assets · {formatDate(project.updated_at)}</span></div>
                  <span className="text-xl text-[#ff6b4a]" aria-hidden="true">→</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
